/**
 * Generic function to process list items and fetch missing data based on a configurable filter
 * @param {Object} params - Parameters object
 * @param {Object} params.selectedListItems - Object containing selected list items
 * @param {Map} params.itemDataMap - Map containing cached data
 * @param {Function} params.fetchListProducts - Function to fetch product data
 * @param {Function} params.updateFunction - Generic function to update data
 * @param {string} params.storeId - Store ID for the request
 * @param {string} params.storeZip - Store ZIP code for the request
 * @param {Function} params.filterCondition - Function that returns true if item needs data fetching
 * @param {number} params.batchSizeLimit - Maximum number of items to process in one batch
 */
export const processListItemsForDataUpdate = ({
  selectedListItems,
  itemDataMap,
  fetchListProducts,
  updateFunction,
  storeId,
  storeZip,
  filterCondition,
  batchSizeLimit = 16
}) => {
  const listItems = Object.values(selectedListItems);
  const listItemsWithMissingData = listItems.filter(
    (items) => items.some(filterCondition)
  );

  if (listItemsWithMissingData.length > 0) {
    const firstListItems = listItemsWithMissingData[0]?.filter(filterCondition);
    const itemIds = firstListItems.slice(0, batchSizeLimit)?.map((item) => item.itemId);
    const itemsKey = itemIds.join(':');

    if (itemDataMap.has(itemsKey)) {
      updateFunction(itemDataMap.get(itemsKey));
    } else {
      fetchListProducts({
        variables: {
          itemIds,
          storeId,
          zipCode: storeZip
        }
      });
    }
  }
};

/**
 * Generic function to process product data response and update cached data
 * @param {Object} params - Parameters object
 * @param {Array} params.itemIds - Array of item IDs from the request variables
 * @param {Array} params.products - Array of product data from the response
 * @param {Function} params.dataExtractor - Function to extract the desired data from each product
 * @param {Function} params.updateFunction - Function to update the data (e.g., updateBasePrice)
 * @param {Function} params.setDataMap - Setter function for the data map state
 * @param {boolean} params.error - Whether there was an error in the request
 */
export const processProductDataResponse = ({
  itemIds,
  products = [],
  dataExtractor,
  updateFunction,
  setDataMap,
  error = false
}) => {
  if (itemIds && (products.length > 0 || Boolean(error))) {
    const productsMap = new Map();
    products.forEach((product) => productsMap.set(product.itemId, product));

    const itemDataMap = new Map();
    itemIds.forEach((itemId) => {
      const product = productsMap.get(itemId);
      const extractedData = dataExtractor(product);
      itemDataMap.set(itemId, extractedData);
    });

    updateFunction(itemDataMap);
    setDataMap((prevMap) => {
      const newMap = new Map(prevMap);
      const itemsKey = itemIds.join(':');
      newMap.set(itemsKey, itemDataMap);
      return newMap;
    });
  }
};

/**
 * Consolidates duplicate items from selected list items by itemId and sums their quantities
 * @param {Object} selectedListItems - Object containing selected list items from different lists
 * @returns {Array} Array of consolidated items with unique itemIds and summed quantities
 */
export const consolidateSelectedItems = (selectedListItems) => {
  const selectedProductsToAdd = Object.values(selectedListItems).flat();

  if (selectedProductsToAdd?.length === 0) {
    return [];
  }

  const itemMap = new Map();
  selectedProductsToAdd.forEach((item) => {
    if (itemMap.has(item.itemId)) {
      const existingItem = itemMap.get(item.itemId);
      const modifiedItem = { ...existingItem, quantity: existingItem.quantity + item.quantity };
      itemMap.set(item.itemId, modifiedItem);
    } else {
      itemMap.set(item.itemId, item);
    }
  });

  return Array.from(itemMap.values());
};

/**
 * Updates selected list items with new data based on a configurable property and condition
 * @param {Function} setSelectedListItems - State setter function for selectedListItems
 * @param {Map} dataMap - Map containing the new data keyed by itemId
 * @param {string} propertyName - Name of the property to update (e.g., 'basePrice', 'sku')
 * @param {*} notFetchedValue - Value that indicates data hasn't been fetched yet
 * @returns {Function} Function that can be used as an update handler
 */
const applyPropertyUpdate = (prevItems, dataMap, propertyName, notFetchedValue) => {
  const updatedSelectedListItems = {};
  Object.keys(prevItems).forEach((prevItemListId) => {
    updatedSelectedListItems[prevItemListId] = prevItems[prevItemListId]?.map((item) => {
      if (item[propertyName] === notFetchedValue) {
        const newValue = dataMap.get(item.itemId) || notFetchedValue;
        return { ...item, [propertyName]: newValue };
      }
      return item;
    });
  });
  return updatedSelectedListItems;
};

export const updateSelectedListItemsProperty = (setSelectedListItems, dataMap, propertyName, notFetchedValue) => {
  return () => {
    setSelectedListItems((prevItems) => applyPropertyUpdate(prevItems, dataMap, propertyName, notFetchedValue));
  };
};
